<template>
  <!-- Icon rail for data-heavy screens (Lavash "Nav/Sidebar Collapsed=On"):
       64px, icons only, labels as tooltips, no expand toggle. -->
  <nav v-if="collapsed" class="app-rail" aria-label="Main">
    <router-link to="/" class="app-rail__brand" aria-label="Marketing — Eidolon analytics" data-tooltip="Marketing">
      <span class="app-brand-mark">M</span>
    </router-link>

    <div class="app-rail__group">
      <router-link v-for="item in navItems" :key="item.to" :to="item.to" class="app-rail__item"
        :aria-current="isActive(item.to) ? 'page' : undefined" :aria-label="item.railLabel"
        :data-tooltip="item.railLabel" @click="emit('navigate')">
        <AtlasIcon :name="item.icon" />
        <span v-if="item.count" class="icon-btn-dot"></span>
      </router-link>
    </div>

    <div class="app-rail__foot">
      <button class="app-rail__item" type="button" aria-label="Sign out" data-tooltip="Sign out" @click="logout">
        <AtlasIcon name="log-out" />
      </button>
      <div class="app-rail__divider"></div>
      <span class="avatar avatar--sm app-rail__avatar" :aria-label="`${email} · ${roleLabel}`"
        :data-tooltip="`${email} · ${roleLabel}`">{{ initials(email) }}</span>
    </div>
  </nav>

  <nav v-else class="sidenav app-sidenav" aria-label="Main">
    <div class="app-brand">
      <span class="app-brand-mark">M</span>
      <span class="app-brand-text">
        <span class="app-brand-name">Marketing</span>
        <span class="app-brand-sub">Eidolon analytics</span>
      </span>
    </div>

    <div class="sidenav-group">
      <div class="sidenav-label">Workspace</div>
      <router-link v-for="item in workspaceItems" :key="item.to" :to="item.to" class="sidenav-item"
        :aria-current="isActive(item.to) ? 'page' : undefined" @click="emit('navigate')">
        <AtlasIcon class="sidenav-icon" :name="item.icon" />
        <span>{{ item.label }}</span>
        <span v-if="item.count" class="sidenav-count">{{ item.count }}</span>
      </router-link>
    </div>

    <div v-if="isAdmin" class="sidenav-group">
      <div class="sidenav-label">Admin</div>
      <router-link to="/users" class="sidenav-item" :aria-current="isActive('/users') ? 'page' : undefined"
        @click="emit('navigate')">
        <AtlasIcon class="sidenav-icon" name="users" />
        <span>Users</span>
      </router-link>
    </div>

    <div class="sidenav-footer">
      <span class="avatar">{{ initials(email) }}</span>
      <span class="sidenav-footer-text">
        <span class="sidenav-footer-name app-footer-email" :title="email">{{ email }}</span>
        <span class="sidenav-footer-meta">{{ roleLabel }}</span>
      </span>
      <button class="icon-btn app-logout" type="button" title="Sign out" aria-label="Sign out" @click="logout">
        <AtlasIcon name="log-out" />
      </button>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import AuthStore from '@/store/AuthStore'
import { RootRoute, RootRouter } from '@/router'
import AtlasIcon from '@/component/ui/AtlasIcon.vue'
import { IconName } from '@/component/ui/icons'
import { initials } from '@/lib/format'

const props = defineProps<{
  runningJobs: number
  collapsed?: boolean
}>()

const emit = defineEmits<{
  (e: 'navigate'): void
}>()

const route = useRoute()

const email = computed(() => AuthStore.module.email ?? '')
const isAdmin = computed(() => AuthStore.module.isAdmin)
const roleLabel = computed(() => {
  const role = AuthStore.module.role ?? ''
  return role.charAt(0).toUpperCase() + role.slice(1)
})

interface NavItem {
  to: string
  label: string
  icon: IconName
  count?: string
}

const workspaceItems = computed(() => {
  const items: NavItem[] = [{ to: '/', label: 'Data', icon: 'chart-column' }]
  if (AuthStore.module.canSeeJobs) {
    items.push({
      to: '/jobs',
      label: 'Jobs',
      icon: 'clock',
      count: props.runningJobs > 0 ? `${props.runningJobs} running` : undefined,
    })
  }
  items.push({ to: '/fb-api-stats', label: 'FB API stats', icon: 'activity' })
  return items
})

// Flat list for the rail: workspace items plus Users for admins.
const navItems = computed(() => {
  const items = workspaceItems.value.map(i => ({
    ...i,
    railLabel: i.count ? `${i.label} · ${i.count}` : i.label,
  }))
  if (isAdmin.value) items.push({ to: '/users', label: 'Users', icon: 'users', railLabel: 'Users' })
  return items
})

function isActive(to: string): boolean {
  return route.path === to
}

function logout() {
  AuthStore.module.logout()
  RootRouter.push(RootRoute.login('/'))
}
</script>

<style scoped>
.app-sidenav { height: 100%; }
.app-brand { display: flex; align-items: center; gap: 10px; padding: 8px 10px 14px; }
.app-brand-mark {
  width: 24px; height: 24px; border-radius: var(--radius-md);
  display: inline-grid; place-items: center;
  background: var(--fg); color: var(--bg);
  font-size: var(--text-sm); font-weight: 600;
}
.app-brand-text { display: flex; flex-direction: column; line-height: 1.2; }
.app-brand-name { font-size: var(--text-md); font-weight: 600; }
.app-brand-sub { font-size: var(--text-sm); color: var(--fg-muted); }
.sidenav-item { height: 32px; }
.sidenav-footer .avatar { background: var(--muted); color: var(--fg); }
.app-footer-email { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sidenav-footer-text { flex: 1; }
.app-logout { flex-shrink: 0; }

.app-rail {
  width: 64px; height: 100%; flex-shrink: 0;
  display: flex; flex-direction: column; align-items: center;
  padding: 12px 0;
  background: var(--side);
  border-right: 1px solid var(--border);
}
.app-rail__brand { display: inline-grid; place-items: center; width: 40px; height: 36px; margin-bottom: 12px; }
.app-rail__brand .app-brand-mark { width: 28px; height: 28px; font-size: var(--text-base); }
.app-rail__group, .app-rail__foot { display: flex; flex-direction: column; align-items: center; gap: 2px; }
.app-rail__foot { margin-top: auto; gap: 8px; }
.app-rail__item {
  position: relative;
  display: inline-grid; place-items: center;
  width: 40px; height: 36px;
  border: 0; border-radius: var(--radius-md);
  background: transparent; color: var(--fg-muted);
  transition: background var(--duration-fast) var(--ease-standard), color var(--duration-fast) var(--ease-standard);
}
.app-rail__item:hover { background: var(--hover); color: var(--fg); }
.app-rail__item:focus-visible { outline: none; box-shadow: var(--ring); }
.app-rail__item[aria-current="page"] { background: var(--accent-soft); color: var(--accent-ink); }
.app-rail__item .icon-btn-dot { top: 7px; right: 9px; border-color: var(--side); }
.app-rail__divider { width: 40px; height: 1px; background: var(--border); }
.app-rail__avatar { background: var(--accent-soft); color: var(--accent-ink); }

/* Labels as tooltips (Atlas Overlay/Tooltip), to the right of the rail. */
.app-rail [data-tooltip] { position: relative; }
.app-rail [data-tooltip]:hover::after,
.app-rail [data-tooltip]:focus-visible::after {
  content: attr(data-tooltip);
  position: absolute; left: calc(100% + 10px); top: 50%; transform: translateY(-50%);
  z-index: 950; pointer-events: none;
  padding: 4px 8px; border-radius: var(--radius);
  background: var(--fg); color: var(--bg);
  font-size: var(--text-sm); font-weight: 500; white-space: nowrap;
  box-shadow: var(--shadow-md);
}
</style>
